<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import DriveAPI from 'dashboard/api/drive';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contentType: {
    type: String,
    required: true,
  },
  currentUrl: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:url']);
const { t } = useI18n();

const fileInput = ref(null);
const uploading = ref(false);
const uploadedFile = ref(null);

const acceptedTypes = computed(() => {
  switch (props.contentType) {
    case 'image':
      return 'image/*';
    case 'video':
      return 'video/*';
    default:
      return '*/*';
  }
});

const placeholderText = computed(() => {
  switch (props.contentType) {
    case 'image':
      return t('WEBSITE.UPLOADER.PLACEHOLDER_IMAGE');
    case 'video':
      return t('WEBSITE.UPLOADER.PLACEHOLDER_VIDEO');
    default:
      return t('WEBSITE.UPLOADER.PLACEHOLDER_FILE');
  }
});

const triggerFileInput = () => {
  fileInput.value?.click();
};

const handleFileSelect = async event => {
  const file = event.target.files[0];
  if (!file) return;

  uploading.value = true;

  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('drive_file[name]', file.name);
    formData.append('drive_file[folder_path]', '/website');

    const response = await DriveAPI.create(formData);
    uploadedFile.value = response.data;

    // La réponse peut avoir file_url, share_url ou url
    const fileUrl =
      response.data?.file_url || response.data?.share_url || response.data?.url;

    if (!fileUrl) {
      console.error('Upload response:', response.data);
      throw new Error('No file URL in response');
    }

    // Émettre l'URL du fichier uploadé
    emit('update:url', fileUrl);

    useAlert(t('DRIVE.API.SUCCESS.FILE_UPLOADED', { name: file.name }));
  } catch (error) {
    console.error('File upload error:', error);
    const errorMessage =
      error.response?.data?.error ||
      error.message ||
      t('DRIVE.API.ERROR.UPLOAD_FAILED', { name: file.name });
    useAlert(errorMessage);
  } finally {
    uploading.value = false;
    event.target.value = '';
  }
};

const removeFile = () => {
  uploadedFile.value = null;
  emit('update:url', '');
};

const currentFileUrl = computed(() => {
  if (props.currentUrl) return props.currentUrl;
  if (!uploadedFile.value) return null;
  // La réponse peut avoir file_url, share_url ou url
  return (
    uploadedFile.value.file_url ||
    uploadedFile.value.share_url ||
    uploadedFile.value.url
  );
});
</script>

<template>
  <div class="space-y-3">
    <!-- Zone d'upload -->
    <div
      v-if="!currentFileUrl"
      class="border-2 border-dashed border-n-weak rounded-lg p-6 text-center hover:border-n-blue-6 hover:bg-n-blue-2/50 transition-all cursor-pointer"
      @click="triggerFileInput"
    >
      <div v-if="uploading" class="flex flex-col items-center gap-2">
        <Spinner />
        <span class="text-sm text-n-slate-11">{{
          t('WEBSITE.UPLOADER.UPLOADING')
        }}</span>
      </div>
      <div v-else class="flex flex-col items-center gap-2">
        <span class="i-lucide-upload size-8 text-n-slate-10" />
        <p class="text-sm text-n-slate-11">
          {{ placeholderText }}
        </p>
        <Button
          :label="t('WEBSITE.UPLOADER.BROWSE')"
          icon="i-lucide-folder-open"
          size="sm"
          color="slate"
          variant="outline"
        />
      </div>
    </div>

    <!-- Fichier uploadé ou URL existante -->
    <div v-else class="border border-n-weak rounded-lg p-4 bg-n-solid-2">
      <div class="flex items-start gap-3">
        <!-- Preview pour les images -->
        <img
          v-if="contentType === 'image' && currentFileUrl"
          :src="currentFileUrl"
          alt="Preview"
          class="w-20 h-20 object-cover rounded border border-n-weak flex-shrink-0"
          @error="$event.target.style.display = 'none'"
        />

        <!-- Icône pour les autres types -->
        <div
          v-else
          class="w-12 h-12 rounded bg-n-purple-2 dark:bg-n-purple-3 flex items-center justify-center flex-shrink-0"
        >
          <span
            class="size-6 text-n-purple-10"
            :class="[
              contentType === 'video' ? 'i-lucide-video' : 'i-lucide-file',
            ]"
          />
        </div>

        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-n-slate-12 truncate">
            {{ uploadedFile?.name || currentFileUrl }}
          </p>
          <p class="text-xs text-n-slate-10 mt-1">
            {{
              uploadedFile?.human_file_size ||
              t('WEBSITE.UPLOADER.EXTERNAL_FILE')
            }}
          </p>
        </div>

        <button
          class="p-2 rounded-lg hover:bg-n-ruby-3 text-n-slate-11 hover:text-n-ruby-11 transition-all flex-shrink-0"
          :title="t('WEBSITE.UPLOADER.DELETE')"
          @click="removeFile"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>
    </div>

    <!-- Input file caché -->
    <input
      ref="fileInput"
      type="file"
      :accept="acceptedTypes"
      class="hidden"
      @change="handleFileSelect"
    />
  </div>
</template>
